'use client';

import React, {useCallback} from 'react';
import classNames from 'classnames';
import {useEffect, useRef, useState} from 'react';
import {
    CLPPurchaseOption,
    useClpPurchaseContext,
    useClpSelectedOption,
} from '@/features/course-landing-page/context/clp-purchase-context.react-component';
import {BlockList} from '@udemy-v2/react-core-components';
import {useCommonData} from '@udemy-v2/common-data';
import {useClpNoticeContext} from '@/features/course-landing-page/context/clp-notice-context-provider.react-component';
import {publishEvent} from '@udemy-v2/event-tracking';
import {ExperimentImpressionEvent} from '@udemy-v2/react-experimentation';
import {Icon} from '@udemy-v2/icons';
import styles from './subscription-purchase-content.module.scss';
import {LocalizedHtml, useFormatNumber, useI18n} from '@udemy-v2/i18n';
import {CollapsiblePurchaseSection} from '@/features/course-landing-page/components/collapsible-purchase-section/collapsible-purchase-section.react-component';
import {
    aggregateCollectionStats,
    FALLBACK_COLLECTION_STATS,
    PlanPeriod,
    PlanPeriodStyle,
    SubscribeFromPageCookie,
    SubscriptionCTA,
    SubscriptionCTAClickEvent,
    SubscriptionLearnMoreEvent,
    SubscriptionOfferPresented,
    usePersonalPlanCollectionStats,
} from '@udemy-v2/react-subscription-browse';
import {SubscriptionPlanProductType} from '@udemy-v2/subscription-graphql';
import {useClpCourse} from '@/features/course-landing-page/context/clp-course-provider.react-component';
import {useClpSubscriptionContext} from '@/features/course-landing-page/context/clp-subscription-context.react-component';
import {BlendedOfferBadge} from '@/features/course-landing-page/components/blended-offer-badge/blended-offer-badge.react-component';
import {LifetimeAccessModal} from '@/features/course-landing-page/components/lifetime-access-modal/lifetime-access-modal.react-component';
import {PersonalPlanIncludesModal} from '@/features/course-landing-page/components/personal-plan-includes-modal/personal-plan-includes-modal.react-component';
import style from '../non-catalog-pp-offer/non-catalog-pp-offer.module.scss';
import {useFeatures} from '@udemy-v2/react-experimentation';
import {
    CollapsiblePurchaseBoxContent,
    SUBSCRIPTION_SUB_VALUE_PROP_MSG,
} from './collapsible-purchase-box-content.react-component';

const UI_REGION = 'purchase_section_tab0';
const PERSONAL_PLAN_COURSE_COUNT = 27000;
const DEFAULT_PERSONAL_PLAN_LEARNER_COUNT = 559000;
const SUBSCRIPTION_ATTRIBUTION_PATHS = ['/personal-plan', '/pricing'];
interface CategoryStats {
    isSubCopyEnabled?: boolean;
    personalPlanLearnerCount?: number;
}

const CollapsibleSubscriptionPurchaseContentInner = (categoryStats: CategoryStats) => {
    const {gettext, interpolate} = useI18n();
    const {formatNumber} = useFormatNumber();
    const {
        unwrappedConsumerSubscriptionPlan,
        subscriptionCTAConfiguration,
        subscriptionTiersEnabled,
        blendedOfferVariant,
        isEligibleForBlendedOffer,
        blendedOfferFeature,
        clpGenericUdemyPlansCopyEnabled,
        starterPlanUpsellCLPCopyUpdatesEnabled,
    } = useClpPurchaseContext();
    const {selectedPurchaseOption, setSelectedPurchaseOption} = useClpSelectedOption();
    const {featureVariantAssignments} = useFeatures([
        'change_cta_language_for_pp',
        'pp_value_prop_update',
    ]);
    const changeCtaLangFeatureAssignment = featureVariantAssignments.get(
        'change_cta_language_for_pp',
    )?.configuration as {getStarted: boolean; getPersonalPlan: boolean} | undefined;
    const ppValuePropConfig = featureVariantAssignments.get('pp_value_prop_update')
        ?.configuration as {variant1?: boolean; variant2?: boolean} | undefined;
    const learnMoreInNewTab = ppValuePropConfig?.variant1 ?? false;
    const learnMoreInModal = ppValuePropConfig?.variant2 ?? false;
    const showPersonalPlanValueProp = learnMoreInNewTab || learnMoreInModal;

    const {isCourseInValuePlanCatalog, subscribedProductType} = useClpSubscriptionContext();
    const isGwgStandaloneSubscriber =
        subscribedProductType === SubscriptionPlanProductType.GrowWithGoogle;

    const course = useClpCourse();
    const commonData = useCommonData();
    const clpNoticeContext = useClpNoticeContext();
    const subscriptionPriceBoxNotice = clpNoticeContext?.subscriptionPriceBoxNoticeData;
    const contentCollections = unwrappedConsumerSubscriptionPlan?.contentCollections;
    const subscriptionPlanStats = contentCollections
        ? aggregateCollectionStats(
              contentCollections.map((contentCollection) => contentCollection.stats),
          )
        : FALLBACK_COLLECTION_STATS;

    const personalPlanStats = usePersonalPlanCollectionStats();
    const personalPlanCourseCount =
        personalPlanStats && 'numberOfCourses' in personalPlanStats
            ? personalPlanStats.numberOfCourses
            : isGwgStandaloneSubscriber && contentCollections
              ? subscriptionPlanStats.numberOfCourses
              : PERSONAL_PLAN_COURSE_COUNT;

    const isPersonalPlanPrimaryPurchaseOptions =
        selectedPurchaseOption === CLPPurchaseOption.SUBSCRIPTION;

    const isStarterCourseInTiers = subscriptionTiersEnabled && isCourseInValuePlanCatalog;

    const hidePersonalPlanValuePropForStarterUpsell =
        isStarterCourseInTiers && starterPlanUpsellCLPCopyUpdatesEnabled;

    const formattedDiscountMonthlyPrice =
        unwrappedConsumerSubscriptionPlan?.selectedDiscount
            ?.formattedSubscriptionDiscountMonthlyPrice;

    const learnMoreText = subscriptionPriceBoxNotice?.subtitle_link_text ?? gettext('Learn more');
    const subscriptionContextLearnMoreUrl =
        unwrappedConsumerSubscriptionPlan?.urlLearnMore ?? 'https://udemy.com/personal-plan/';
    const learnMoreUrl = isStarterCourseInTiers
        ? 'https://udemy.com/pricing/'
        : subscriptionPriceBoxNotice?.subtitle_link_url ?? subscriptionContextLearnMoreUrl;
    const learnMoreTarget =
        learnMoreInNewTab || subscriptionPriceBoxNotice?.subtitle_link_target === 'new_tab'
            ? '_blank'
            : '_self';
    const handleLearnMoreClick = () => {
        // When navigating to Personal Plan info / plan selection pages from CLP, set the
        // subscribe_from_page cookie so subscription checkout can attribute to this course.
        if (SUBSCRIPTION_ATTRIBUTION_PATHS.some((path) => learnMoreUrl.includes(path))) {
            SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        }

        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionLearnMoreEvent({
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planType: unwrappedConsumerSubscriptionPlan.productType,
                    uiRegion: UI_REGION,
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                }),
            );
        }
    };

    const handleCtaClick = () => {
        SubscribeFromPageCookie.set('course_landing_page', parseInt(course.id, 10));
        if (unwrappedConsumerSubscriptionPlan) {
            publishEvent(
                new SubscriptionCTAClickEvent({
                    planId: unwrappedConsumerSubscriptionPlan.id,
                    planType: unwrappedConsumerSubscriptionPlan.productType,
                    ctaType: unwrappedConsumerSubscriptionPlan.trialPeriodDays ? 'trial' : 'paid',
                    uiRegion: UI_REGION,
                    courseId: parseInt(course.id, 10),
                    planPriceIds: unwrappedConsumerSubscriptionPlan.planPriceOptionIds,
                }),
            );
        }
    };

    // With the Tiers flag enabled, we want to fire SubscriptionOfferPresented instead of SubcriptionOfferImpressionEvent as it has a productType field that lets DS differentiate between Starter Plan CLPs and Personal Plan CLPs
    const handleOfferImpression =
        subscriptionTiersEnabled && unwrappedConsumerSubscriptionPlan
            ? () => {
                  publishEvent(
                      new SubscriptionOfferPresented({
                          subscriptionPlan: unwrappedConsumerSubscriptionPlan,
                          productType: unwrappedConsumerSubscriptionPlan.productType,
                          countryCode: commonData?.user?.country ?? '',
                          courseId: parseInt(course.id, 10),
                          courseTrackingId: course.frontendTrackingId ?? null,
                          uiRegion: UI_REGION,
                      }),
                  );
              }
            : undefined;

    const handleExpand = useCallback(
        () => setSelectedPurchaseOption(CLPPurchaseOption.SUBSCRIPTION),
        [setSelectedPurchaseOption],
    );

    const showBlendedOfferBadge = isEligibleForBlendedOffer && !!blendedOfferVariant;
    const [isLifetimeAccessModalOpen, setIsLifetimeAccessModalOpen] = useState(false);
    const [isPersonalPlanIncludesModalOpen, setIsPersonalPlanIncludesModalOpen] = useState(false);
    const hasFiredBlendedOfferImpressionRef = useRef(false);
    useEffect(() => {
        if (showBlendedOfferBadge && !hasFiredBlendedOfferImpressionRef.current) {
            hasFiredBlendedOfferImpressionRef.current = true;
            publishEvent(
                new ExperimentImpressionEvent({
                    experimentIds: blendedOfferFeature?.experimentIds ?? [],
                    isLocalDefaultShown: blendedOfferFeature?.isLocalDefault ?? false,
                }),
            );
        }
    }, [showBlendedOfferBadge, blendedOfferFeature]);

    if (!unwrappedConsumerSubscriptionPlan) {
        return null;
    }

    const titleText = gettext('Subscribe and save');

    const openIncludesModal = (event: React.SyntheticEvent) => {
        event.stopPropagation();
        setIsPersonalPlanIncludesModalOpen(true);
    };

    const title = (
        <>
            <span className={styles['title-row']}>
                <span className={classNames('ud-text-md')}>{titleText}</span>
                {learnMoreInModal && (
                    <span
                        role="button"
                        tabIndex={0}
                        aria-label={gettext("What's included in Personal Plan")}
                        data-purpose="personal-plan-includes-trigger"
                        className={styles['includes-info-trigger']}
                        onClick={openIncludesModal}
                        onKeyDown={(event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                openIncludesModal(event);
                            }
                        }}
                    >
                        <Icon glyph="info-outline" label="" size="xsmall" />
                    </span>
                )}
            </span>
            {showBlendedOfferBadge && blendedOfferVariant === 'annual' ? (
                <p>
                    <span className="ud-heading-xl">
                        {unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount}
                    </span>
                    <span className="ud-text-sm">{gettext('/month, billed yearly')}</span>
                </p>
            ) : (
                <PlanPeriod
                    listPriceText={unwrappedConsumerSubscriptionPlan.formattedMonthlyAmount}
                    trialPeriodDays={unwrappedConsumerSubscriptionPlan.trialPeriodDays}
                    style={PlanPeriodStyle.TITLE}
                    discountPriceText={formattedDiscountMonthlyPrice}
                />
            )}
        </>
    );

    const ctaText = (
        config: {getStarted: boolean; getPersonalPlan: boolean} | undefined,
    ): string => {
        if (!config) {
            return gettext('Start subscription');
        }
        if (config.getStarted && !config.getPersonalPlan) {
            return gettext('Get started');
        }
        if (!config.getStarted && config.getPersonalPlan) {
            return gettext('Get Personal Plan');
        }
        return gettext('Start subscription');
    };

    return (
        <CollapsiblePurchaseSection
            expanded={isPersonalPlanPrimaryPurchaseOptions}
            title={title}
            onExpand={handleExpand}
        >
            {showBlendedOfferBadge && blendedOfferVariant && (
                <>
                    <BlendedOfferBadge
                        variant={blendedOfferVariant}
                        termsUrl={unwrappedConsumerSubscriptionPlan?.urlTermsOfUse}
                        onLifetimeAccessClick={() => setIsLifetimeAccessModalOpen(true)}
                    />
                    <LifetimeAccessModal
                        isOpen={isLifetimeAccessModalOpen}
                        onClose={() => setIsLifetimeAccessModalOpen(false)}
                    />
                </>
            )}
            <BlockList padding="tight" size="small">
                {categoryStats.isSubCopyEnabled && (
                    <div className={styles['value-prop-badge']}>
                        <Icon
                            className={style['value-prop-icon']}
                            glyph="info-outline"
                            color="info"
                            size="small"
                            label=""
                        />
                        <LocalizedHtml
                            html={gettext(SUBSCRIPTION_SUB_VALUE_PROP_MSG)}
                            interpolate={{
                                bold: <span className="ud-text-bold" />,
                            }}
                        />
                    </div>
                )}
                {categoryStats.isSubCopyEnabled && (
                    <CollapsiblePurchaseBoxContent
                        coursesCount={formatNumber(
                            isStarterCourseInTiers
                                ? subscriptionPlanStats.numberOfCourses
                                : subscriptionTiersEnabled
                                  ? personalPlanCourseCount
                                  : subscriptionPlanStats.numberOfCourses,
                        )}
                        categoryName={course.categories[0]?.name}
                    />
                )}
                {!categoryStats.isSubCopyEnabled && (
                    <BlockList.Item
                        icon={
                            <Icon
                                label=""
                                size="xsmall"
                                glyph={showBlendedOfferBadge ? 'tick' : 'video-playlist'}
                            />
                        }
                    >
                        <span className="ud-text-sm">
                            {showBlendedOfferBadge
                                ? interpolate(
                                      gettext('Access %(courses_count)s+ Premium courses'),
                                      {
                                          courses_count: formatNumber(
                                              isStarterCourseInTiers
                                                  ? subscriptionPlanStats.numberOfCourses
                                                  : subscriptionTiersEnabled
                                                    ? personalPlanCourseCount
                                                    : subscriptionPlanStats.numberOfCourses,
                                          ),
                                      },
                                      true,
                                  )
                                : isStarterCourseInTiers && clpGenericUdemyPlansCopyEnabled
                                  ? interpolate(
                                        gettext(
                                            'Access up to %(courses_count)s+ courses with a Udemy subscription.',
                                        ),
                                        {courses_count: formatNumber(personalPlanCourseCount)},
                                        true,
                                    )
                                  : interpolate(
                                        gettext('Access to %(courses_count)s+ top-rated courses'),
                                        {
                                            courses_count: formatNumber(
                                                isStarterCourseInTiers
                                                    ? subscriptionPlanStats.numberOfCourses
                                                    : subscriptionTiersEnabled
                                                      ? personalPlanCourseCount
                                                      : subscriptionPlanStats.numberOfCourses,
                                            ),
                                        },
                                        true,
                                    )}
                        </span>
                    </BlockList.Item>
                )}
                <BlockList.Item
                    icon={
                        <Icon
                            label=""
                            size="xsmall"
                            glyph={
                                showBlendedOfferBadge
                                    ? 'tick'
                                    : subscriptionTiersEnabled
                                      ? 'cancel'
                                      : 'coupon'
                            }
                        />
                    }
                >
                    <span className="ud-text-sm">{gettext('Cancel anytime')}</span>
                </BlockList.Item>
                {!categoryStats.isSubCopyEnabled && !learnMoreInModal && (
                    <BlockList.Item icon={<Icon label="" size="xsmall" glyph="info-outline" />}>
                        <a
                            className={
                                learnMoreInNewTab
                                    ? styles['learn-more-link-default']
                                    : showBlendedOfferBadge
                                      ? styles['learn-more-link-blended']
                                      : styles['learn-more-link']
                            }
                            data-purpose="subscription-learn-more-link"
                            href={learnMoreUrl}
                            target={learnMoreTarget}
                            onClick={handleLearnMoreClick}
                        >
                            {learnMoreInNewTab
                                ? gettext('Learn more about Udemy subscriptions')
                                : showBlendedOfferBadge
                                  ? gettext('Learn more about Personal Plan')
                                  : learnMoreText}
                        </a>
                    </BlockList.Item>
                )}
            </BlockList>
            <SubscriptionCTA
                className={classNames(
                    styles['subscription-cta'],
                    styles['subscription-cta-collapsible'],
                )}
                uiRegion={UI_REGION}
                showSubtitle={
                    !hidePersonalPlanValuePropForStarterUpsell &&
                    (showPersonalPlanValueProp || categoryStats.isSubCopyEnabled)
                }
                data-purpose="subscription-purchase-button"
                offerCtaStyle={'primary'}
                onCTAClick={handleCtaClick}
                trackImpression={handleOfferImpression}
                courseId={parseInt(course.id, 10)}
                includeTrialDaysOnCta={false}
                plan={unwrappedConsumerSubscriptionPlan}
                noticeData={
                    subscriptionCTAConfiguration?.upgradeUrl
                        ? {
                              ...subscriptionPriceBoxNotice,
                              cta_1_url: subscriptionCTAConfiguration.upgradeUrl,
                          }
                        : subscriptionPriceBoxNotice
                }
                directCheckout={subscriptionCTAConfiguration?.directCheckout}
                selectPlanText={subscriptionCTAConfiguration?.selectPlanText}
                customSubtitle={
                    showPersonalPlanValueProp
                        ? interpolate(
                              gettext('Join %(learner_count)s learners on Personal Plan'),
                              {
                                  learner_count: formatNumber(
                                      categoryStats.personalPlanLearnerCount ??
                                          DEFAULT_PERSONAL_PLAN_LEARNER_COUNT,
                                  ),
                              },
                              true,
                          )
                        : 'Join 5 lakh+ learners on Personal Plan today'
                }
                purchaseCtaText={
                    subscriptionCTAConfiguration?.purchaseCtaText ||
                    ctaText(changeCtaLangFeatureAssignment)
                }
            />
            {learnMoreInModal && (
                <PersonalPlanIncludesModal
                    isOpen={isPersonalPlanIncludesModalOpen}
                    onClose={() => setIsPersonalPlanIncludesModalOpen(false)}
                    courseCount={personalPlanCourseCount}
                />
            )}
        </CollapsiblePurchaseSection>
    );
};

export const CollapsibleSubscriptionPurchaseContent = React.memo(
    CollapsibleSubscriptionPurchaseContentInner,
);
